import { CollectionsHelper } from '@/helpers/collections.helper';
import apiClient from '@/services/api-client';
/**
 * @typedef {import('@/models/employee-assignments.interface').EmployeeAssignments} EmployeeAssignments
 * @typedef {import('@/models/assignment.interface').AssignmentFull} AssignmentFull
 * @typedef {import('@/models/project.interface').ProjectFull} Project
 */
/**
 * @template S, R
 * @typedef {import('vuex').ActionTree<S, R>} ActionTree<S, R>
 */
/**
 * @template S
 * @typedef {import('vuex').MutationTree<S>} MutationTree<S>
 */
/**
 * @template S, R
 * @typedef {import('vuex').GetterTree<S, R>} GetterTree<S, R>
 */
/**
 * @template S, R
 * @typedef {import('vuex').Module<S, R>} Module<S, R>
 */
/**
 * @typedef {import('@/store/root.state').RootState} RootState
 */

/**
 * @typedef {Object} ProjectsMainState
 * @property {Project | {}} project
 * @property {Project[]} projects
 * @property {AssignmentFull[]} projectAssignments
 * @property {boolean} isShowOnlyMyOffice
 * @property {boolean} isShowRemoved
 * @property {string} search
 * @property {EmployeeAssignments[]} employeeAssignments
 */

/** @returns {ProjectsMainState} */
const getInitialState = () => ({
  project: {},
  projects: [],
  projectAssignments: [],
  isShowOnlyMyOffice: true,
  isShowRemoved: false,
  search: '',
  employeeAssignments: [],
  projectSettings: {},
});

/** @type {ProjectsMainState} */
const state = getInitialState();

/** @type {GetterTree<ProjectsMainState, RootState>} */
const getters = {
  projects: (state) => state.projects,
  project: (state) => state.project,
  projectAssignments: (state) => state.projectAssignments,
  isShowOnlyMyOffice: (state) => state.isShowOnlyMyOffice,
  isShowRemoved: (state) => state.isShowRemoved,
  search: (state) => state.search,
  employeeAssignments: (state) => state.employeeAssignments,
  projectSettings: (state) => state.projectSettings,
};

/** @type {ActionTree<ProjectsMainState, RootState>} */
const actions = {
  getProjects: async ({ commit }, { employeeId, officeId }) => {
    const { data } = await apiClient.projectApi.getProjects(employeeId, officeId);

    commit('setProjects', data);
  },
  setProject: ({ commit }, project) => commit('setProject', project),
  getProjectById: async ({ commit }, { projectId }) => {
    const { data } = await apiClient.projectApi.getProjectById(projectId);

    commit('setProject', data);
  },
  getProjectAssignments: async ({ commit }, { projectId, options }) => {
    const { data } = await apiClient.projectApi.getProjectAssignments(projectId, options);

    commit('setProjectAssignments', data);
  },
  setEmployeeProjectRole: (context, { projectId, employeeId, roleId }) => apiClient.projectApi.putEmployeeProjectRole(projectId, employeeId, roleId),
  removeEmployeeFromProject: (context, { projectId, employeeId, roleId }) => apiClient.projectApi.deleteProjectEmployee(projectId, employeeId, roleId),
  setIsShowOnlyMyOffice: ({ commit }, isShowOnlyMyOffice) => commit('setIsShowOnlyMyOffice', isShowOnlyMyOffice),
  setIsShowRemoved: ({ commit }, isShowRemoved) => commit('setIsShowRemoved', isShowRemoved),
  setSearch: ({ commit }, searchText) => commit('setSearch', searchText),
  clearProjectAssignments: ({ commit }) => commit('setProjectAssignments', []),
  getEmployeeAssignments: async ({ commit }, { employeeId, options }) => {
    const { data } = await apiClient.employeeApi.getEmployeeAssignments({ employeeId, options });

    commit('setEmployeeAssignments', data);
  },
  getProjectSettingsById: async ({ commit }, { projectId }) => {
    const { data } = await apiClient.projectApi.getProjectSettingsById(projectId);

    commit('setProjectSettings', data);
  },
  setProjectSettings: ({ commit }, projectSettings) => commit('setProjectSettings', projectSettings),
  updateAllowTimeLogsInFinishedProject: async ({ commit }, { projectId, allowTimeLogsInFinishedProject }) => {
    const { data } = await apiClient.projectApi.updateAllowTimeLogsInFinishedProject(projectId, allowTimeLogsInFinishedProject);
    const projectSettings = { ...state.projectSettings, allow_time_logs_in_finished_project: data };

    commit('setProjectSettings', projectSettings);
  },
  updateAllowTimeLogsForRemovedEmployees: async ({ commit }, { projectId, allowTimeLogsForRemovedEmployees }) => {
    const { data } = await apiClient.projectApi.updateAllowTimeLogsForRemovedEmployees(projectId, allowTimeLogsForRemovedEmployees);
    const projectSettings = { ...state.projectSettings, allow_time_logs_for_removed_employees: data };

    commit('setProjectSettings', projectSettings);
  },
};

/** @type {MutationTree<ProjectsMainState>} */
const mutations = {
  setProject: (state, data) => (state.project = data),
  setProjects: (state, projects) => (state.projects = CollectionsHelper.sortObjectsByProperty(projects, 'title')),
  setProjectAssignments: (state, data) => (state.projectAssignments = data),
  setIsShowOnlyMyOffice: (state, isShowOnlyMyOffice = !state.isShowOnlyMyOffice) => (state.isShowOnlyMyOffice = isShowOnlyMyOffice),
  setIsShowRemoved: (state, isShowRemoved = !state.isShowRemoved) => (state.isShowRemoved = isShowRemoved),
  setSearch: (state, searchText) => (state.search = searchText),
  resetModuleState: (state) => Object.assign(state, getInitialState()),
  setEmployeeAssignments: (state, employeeAssignments) => (state.employeeAssignments = employeeAssignments),
  setProjectSettings: (state, projectSettings) => (state.projectSettings = projectSettings),
};

/** @type {Module<ProjectsMainState, RootState>} */
export const main = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
